import React, {useState, useRef, useEffect} from 'react'
import {VisDiv} from '../components'
import { useOfflineContext } from '../context/OfflineContext'
import { useStateContext } from '../context/StateContext'
import { useUploadPhoto, useCreateReplace } from '../hooks/mutations'
import { successAlert, errorAlert, fromBase64ToSanity, keyId, objectIsObject } from '../lib/utils'
import { useQueryClient } from '@tanstack/react-query'

const OfflineUpdate = ({data={}}) => {
    //#offlineUpdate
    const {registersData} = data
    const {offlineData, setOfflineData, base64Images, setBase64Images} = useOfflineContext()
    const {
        sUser,
        site,
        register,
        setRegister,
        currentDevice,
        setCurrentDevice,
        registers, 
        setRegisters,
        setLoading
    } = useStateContext();

    const logContainerRef = useRef(undefined);
    const [updateLogs, setUpdateLogs] = useState([])
    const queryClient = useQueryClient()
    const [sanityPictures, setSanityPictures] = useState([])

    // Auto-scroll to latest log
    useEffect(() => {
        if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [updateLogs]);

    const updateLogText = (text="Update Eseguito con successo", status = "success" ) => {
        const emoji = {
            success: "✅",
            error: "❌",
            warning: "⚠️",
            info: "ℹ️",
            processing: "⏳",
            photo: "📸",
            saving: "💾",
            network: "🌐",
        }
        
        return {
            log: <div style={{
                color: status === "success" ? "green" : 
                      status === "error" ? "red" : 
                      status === "warning" ? "orange" : 
                      "blue",
                padding: "5px",
                margin: "5px 0",
                fontSize: "14px",
                animation: "fadeIn 0.3s ease-in"
            }}>
                {emoji[status] || emoji.info} {text}
            </div>,
            status,
            text,
            timestamp: new Date().getTime()
        }
    }
    
    const updateLog = (text="Update Eseguito con successo", status = "success" ) => {
        setUpdateLogs(prevLogs => [...!Array.isArray(prevLogs) ? [] : prevLogs, updateLogText(text, status)])
    }

    const deleteOfflineData = () => {
        setOfflineData([])
        updateLog("Dati offline eliminati", "info")
    }

    const downloadOfflineData = () => {
        updateLog("Avvio download dei dati...", "processing")
        const jsonString = JSON.stringify(offlineData, undefined, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'offlineData.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        updateLog("Download dei dati completato", "success")
    }

    const {mutate: updateRegister} = useCreateReplace({
        onError:(e)=> {
            console.log("REGISTER Not UPDATED", e)
            errorAlert("IL REGISTRO NON E' STATO AGGIORNATO, RIPROVARE")
            updateLog("Errore nell'aggiornamento del registro", "error")
            throw e
        }
    })

    const {mutate: uploadPhotoAsset} = useUploadPhoto({

        onError:(e)=>{
            console.error("salvataggio immagine non riuscito:",e.message)
            errorAlert("UPLOAD FOTO NON RIUSCITO")
            updateLog("Errore nel caricamento della foto", "error")
            throw e
        }
    })

    const extractBase64Images = (register) => {
        const commentsWithImages = !Array.isArray(register?.devices) ? [] : register?.devices?.map?.((device)=>{
            return device?.comments
        }).flat().filter?.((x)=>x?.images?.length > 0)
        
        const commentsWithBase64Images = !Array.isArray(commentsWithImages) ? [] : commentsWithImages?.filter?.((x)=>
            x?.images?.some?.((y)=>y?.before?.base64Image || y?.after?.base64Image))
        
        const allBase64Images = !Array.isArray(commentsWithBase64Images) ? [] : commentsWithBase64Images?.map?.((item)=>{
            const images = !Array.isArray(item?.images) ? [] : item?.images?.map?.((image)=> {
                const beforeBase64Image = image?.before?.base64Image ? image?.before : undefined
                const afterBase64Image = image?.after?.base64Image ? image?.after : undefined
                return [beforeBase64Image, afterBase64Image]?.filter?.((x)=>x)
            })
            return images?.flat()
        })?.flat()

        return allBase64Images || []
    }

    const uploadImages = async (images) => {
        const uploadedImages = []
        
        for (const imageDoc of images) {
            try {
                const file = await fromBase64ToSanity(imageDoc?.base64Image?.src)
                const asset = {fileType:"image", file: file?.file}
                
                await new Promise((resolve, reject) => {
                    uploadPhotoAsset(asset, {
                        onSuccess: (data) => {
                            const sanityDoc = {
                                _type: 'image',
                                _key: keyId(),
                                asset: {
                                    _type: 'reference',
                                    _ref: data?._id,
                                    _key: keyId()
                                }
                            }
                            uploadedImages.push({
                                sanityImage: sanityDoc,
                                beforeAfterPhotoId: imageDoc?.beforeAfterPhotoId,
                                beforeOrAfter: imageDoc?.beforeOrAfter
                            })
                            resolve()
                        },
                        onError: reject
                    })
                })
            } catch (error) {
                console.error("Failed to upload image:", error)
                throw error
            }
        }
        
        return uploadedImages
    }

    const updateRegisterWithImages = (register, uploadedImages) => {
        if(!register) {
            updateLog(`Errore registro non trovato ${register?.label || register?._id}`, "error")
            return
        } 
        const newDevices = !Array.isArray(register?.devices) ? [] : register?.devices?.map?.((device)=>{
            const commentsArray = device?.comments ? device?.comments : []
            const updatedComments = !Array.isArray(commentsArray) ? [] : commentsArray?.map?.((comment)=>{
                const commentImages = !Array.isArray(comment?.images) ? [] : comment?.images
                const commentsWithNewImages = !Array.isArray(commentImages) ? [] : commentImages?.map?.((image)=>{
                    const findUpdatedImagesByBeforeAndAfterId = !Array.isArray(uploadedImages) ? [] : uploadedImages?.filter?.((x)=>
                        x?.beforeAfterPhotoId == image?._id)
                    const findNewBeforeImage = !Array.isArray(findUpdatedImagesByBeforeAndAfterId) ? undefined : findUpdatedImagesByBeforeAndAfterId?.find?.((y)=>
                        y?.beforeOrAfter == "before")
                    const findNewAfterImage = !Array.isArray(findUpdatedImagesByBeforeAndAfterId) ? undefined : findUpdatedImagesByBeforeAndAfterId?.find?.((y)=>
                        y?.beforeOrAfter == "after")
                    const newImageDoc = {
                        ... objectIsObject(image),
                        before: findNewBeforeImage?.sanityImage?.asset?._ref ? 
                            {...findNewBeforeImage, base64Image:""} : image?.before,
                        after: findNewAfterImage?.sanityImage?.asset?._ref ? 
                            {...findNewAfterImage, base64Image:""} : image?.after,
                    }
                    console.log(
                        "liuawehfliaweufh", findUpdatedImagesByBeforeAndAfterId, 
                        "beforeImageFound", findNewBeforeImage, 
                        "afterImageFound", findNewAfterImage,
                        "newImageDoc", newImageDoc
                     )
                    return newImageDoc
                })
                return {...comment, images: commentsWithNewImages}
            })
            return {...device, comments: updatedComments}
        })

        return {...register, devices: newDevices}
    }

    const updateRegisterInDB = async (register) => {
        return new Promise((resolve, reject) => {
            updateRegister({doc: register}, {
                onSuccess: resolve,
                onError: reject
            })
        })
    }

    const updateUIState = (successfulUpdates) => {
        // Update current register if it was updated
        const currentUpdatedRegister = !Array.isArray(successfulUpdates) ? undefined : successfulUpdates?.find?.((x)=>x?._id == register?._id)
        const currentUpdatedDevice = !Array.isArray(currentUpdatedRegister?.devices) ? undefined : currentUpdatedRegister?.devices?.find?.((x)=>x?._id == currentDevice?._id)
        
        // Update registers list
        const filteredRegisters = !Array.isArray(registers) ? [] : registers?.filter?.((x)=>!successfulUpdates?.some?.((y)=>y?._id == x?._id))
        const updatedRegisters = [...filteredRegisters, ...successfulUpdates]
        
        // Update React Query cache
        queryClient.cancelQueries({ queryKey: ['registers', site] })
        queryClient.setQueryData(['registers', site], updatedRegisters)
        
        // Update state
        setRegister(currentUpdatedRegister)
        setCurrentDevice(currentUpdatedDevice)
        setRegisters(updatedRegisters)
    }

    const logFilter = (register) => {
        const queryRegister = (register) => !Array.isArray(registersData) ? undefined : registersData?.find?.(y=>y?._id == register?._id)
        const nLogs = (register) => !Array.isArray(register?.devices) ? [] : register?.devices?.map?.((device)=>{
            const deviceLogs = device?.userLog ? device?.userLog : []
            return deviceLogs
        }).flat().length
        
        const obj = {register: nLogs(register), queryRegister: nLogs(queryRegister(register))}
            
        return obj.register > obj.queryRegister || !queryRegister(register) 
    }

    const filterOfflineData = !Array.isArray(offlineData) ? [] : offlineData?.filter?.(logFilter)

    const updateOfflineData = async () => {
        setUpdateLogs([]) // Clear previous logs
        updateLog("Inizio aggiornamento dati offline", "info")
        let successfulUpdates = []
        let failedUpdates = []
        
        try {
            for (const register of filterOfflineData) {
                try {
                    await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for visual feedback
                    updateLog(`Elaborazione registro: ${register.label || register._id}`, "processing")
                    
                    const base64Images = extractBase64Images(register)
                    
                    if(base64Images?.length > 0) {
                        updateLog(`Caricamento ${base64Images.length} foto`, "photo")
                        const uploadedImages = await uploadImages(base64Images)
                        updateLog("Foto caricate con successo", "success")
                        
                        const updatedRegister = await updateRegisterWithImages(register, uploadedImages)
                        
                        updateLog("Salvataggio registro con foto", "saving")
                        updatedRegister && await updateRegisterInDB(updatedRegister)
                        
                        updatedRegister && successfulUpdates.push(updatedRegister)
                        updatedRegister && updateLog(`Registro ${register.label || register._id} aggiornato con successo`, "success")
                        !updatedRegister && updateLog(`Errore nell'aggiornamento del registro ${register.label || register._id}`, "error")
                    } else {
                        updateLog("Salvataggio registro", "saving")
                        await updateRegisterInDB(register)
                        successfulUpdates.push(register)
                        updateLog(`Registro ${register.label || register._id} aggiornato con successo`, "success")
                    }
                } catch (error) {
                    console.error("Failed to update register:", register._id, error)
                    failedUpdates.push(register)
                    updateLog(`Errore nell'aggiornamento del registro ${register.label || register._id}`, "error")
                }
            }

            updateUIState(successfulUpdates)
            setOfflineData(failedUpdates)
            
            if(failedUpdates.length > 0) {
                updateLog(`⚠️ ${failedUpdates.length} registri non aggiornati`, "warning")
                errorAlert("Some updates failed. Failed registers kept in offline data.")
            } else {
                updateLog("✨ Tutti i registri aggiornati con successo", "success")
                successAlert("All updates completed successfully")
                window.location.reload()
            }

        } catch (error) {
            console.error("Update process failed:", error)
            errorAlert("Update process failed")
            updateLog("Processo di aggiornamento fallito", "error")
        }
    }

    const updateTest = () => {
        console.log("offline data", offlineData.filter?.(logFilter))
    }

    return (
        <div className="products-heading stop-scroll-refresh" >
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(-10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}
            </style>
            <VisDiv rules={[sUser?.userType == "God Mode"]}>
                <button onClick={()=>updateTest()}>Test</button>
            </VisDiv> 
            <VisDiv style={{marginBottom:"10px"}}>
                <button style={{backgroundColor:"green"}} 
                        className='btn' 
                        onClick={()=>updateOfflineData()}>
                    AGGIORNA DATI OFFLINE
                </button>
            </VisDiv>  
            <VisDiv style={{marginBottom:"10px"}}>
                <button style={{backgroundColor:"blue"}} 
                        className='btn' 
                        onClick={()=>downloadOfflineData()}>
                    SCARICA DATI OFFLINE (JSON)
                </button>
            </VisDiv>
            <h3>SONO STATI RILEVATI DATI CARICATI IN MODALITA' OFFLINE. 
                SE HAI BUONA CONNESSIONE, CLICCA QUI SOPRA PER AGGIORNARE IL SISTEMA.</h3>
            <p> SE TI TROVI IN UN' AREA CON CONNETTIVITA' INSTABILE, 
               PUOI CONTINUARE A LAVORARE OFFLINE METTENDO IL DISPOSITIVO IN MODALITA' AEREO 
               E AGGIORNARE IL SISTEMA UNA VOLTA RAGGIUNTA UN'AREA CON CONNESSIONE STABILE </p>
            <h3>REGISTRI DA AGGIORNARE:</h3>
            {!Array.isArray(filterOfflineData) ? [] : filterOfflineData?.map?.((item)=>{
                return (
                    <div key={item?._id} > 
                        📄 {`${item?.label} ${item.registerName ? item.registerName : ""}`}
                    </div>
                )
            })}
            <div 
                ref={logContainerRef}
                style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    padding: "10px",
                    margin: "10px 0",
                    border: "1px solid #eee",
                    borderRadius: "5px",
                    backgroundColor: "#fafafa"
                }}
            >
                {!Array.isArray(updateLogs) ? [] : updateLogs?.map?.((x, index)=><div key={`${index}-${x.timestamp}`}>{x?.log}</div>)}
            </div>

            <VisDiv rules={[sUser?.userType == "God Mode"]}>
                <button onClick={()=>deleteOfflineData()}>🗑️ delete offline data</button>
            </VisDiv> 
        </div>
    )
}

export default OfflineUpdate
